import { forwardRef } from 'react';

interface BaseButtonProps {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  disabled?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

type ButtonElementProps = BaseButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement> & {
  href?: undefined;
};

type AnchorElementProps = BaseButtonProps & React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
};

type ButtonProps = ButtonElementProps | AnchorElementProps;

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      children,
      className = '',
      disabled,
      ...props
    },
    ref
  ) => {
    const baseStyles = `
      btn-micro inline-flex items-center justify-center gap-2 font-medium rounded-xl
      transition-all duration-200 ease-out
      focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent
      disabled:opacity-50 disabled:cursor-not-allowed
    `;

    const variants = {
      primary: `
        bg-gradient-to-r from-primary-500 to-accent-500
        hover:from-primary-400 hover:to-accent-400
        text-white shadow-lg shadow-primary-500/25
        hover:shadow-xl hover:shadow-primary-500/30
        active:scale-[0.98]
        focus-visible:ring-primary-400
      `,
      secondary: `
        bg-black/5 dark:bg-white/10 backdrop-blur-sm
        hover:bg-black/10 dark:hover:bg-white/20
        text-parchment-text dark:text-white border border-black/10 dark:border-white/20
        hover:border-black/20 dark:hover:border-white/30
        active:scale-[0.98]
        focus-visible:ring-primary-400 dark:focus-visible:ring-white/50
      `,
      ghost: `
        bg-transparent
        hover:bg-black/5 dark:hover:bg-white/10
        text-parchment-text/80 dark:text-white/80 hover:text-parchment-text dark:hover:text-white
        focus-visible:ring-primary-400 dark:focus-visible:ring-white/50
      `,
    };

    const sizes = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-5 py-2.5 text-base',
      lg: 'px-8 py-4 text-lg',
    };

    const isDisabled = disabled || isLoading;
    const sharedClassName = `${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`;

    if ('href' in props && typeof props.href === 'string') {
      const { href, onClick, ...anchorProps } = props as AnchorElementProps;

      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          className={`${sharedClassName} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          aria-disabled={isDisabled}
          tabIndex={isDisabled ? -1 : anchorProps.tabIndex}
          onClick={(event) => {
            if (isDisabled) {
              event.preventDefault();
              return;
            }
            onClick?.(event);
          }}
          {...anchorProps}
        >
          {isLoading ? (
            <svg
              className="animate-spin h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            leftIcon
          )}
          {children}
          {!isLoading && rightIcon}
        </a>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        className={sharedClassName}
        disabled={isDisabled}
        {...(props as ButtonElementProps)}
      >
        {isLoading ? (
          <svg
            className="animate-spin h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          leftIcon
        )}
        {children}
        {!isLoading && rightIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';
